import {
  CONNECTION_STATUS,
  formatParisDateTime,
  type ConnectionTestResult,
  type ShopifyProduct,
  type StoreDetail,
  type StoreUpdateBody,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { BackLink, PageSection } from '@/components/layout/page.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Button, buttonVariants } from '@/components/ui/button.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { isSameValue } from '@/lib/is-same-value.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import { Switch } from '@/components/ui/switch.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { fetchStoreDetail, installUrl, testStoreConnection, updateStore } from './api-stores.js';
import { ProductSearchPicker } from './product-search.js';
import { settingsKeys } from './query-keys.js';
import { ConnectionTestMessage, SettingsSectionSkeleton } from './settings-parts.js';
import { CatchupSection } from './catchup-section.js';

export interface StoreDetailFormProps {
  code: string;
  onBack: () => void;
}

interface BlockingProductDraft {
  productId: string;
  title: string;
}

interface CustomMessageDraft {
  enabled: boolean;
  productId: string;
  productTitle: string;
  propertyName: string;
  column: 7 | 8;
}

interface StoreDraft {
  code: string;
  name: string;
  mode: StoreDetail['mode'];
  webhookSilenceHours: string;
  blockingProductIds: BlockingProductDraft[];
  customMessage: CustomMessageDraft;
  defaultCarrierCode: string;
  defaultCarrierLabel: string;
}

/** Même lien discret que le `backLink` de `PageHeader` : la fiche vit dans un onglet, pas dans sa propre page. */
function BackToListButton({ onBack }: { onBack: () => void }): JSX.Element {
  return <BackLink backLink={{ label: 'Retour à la liste', onClick: onBack }} />;
}

function toDraft(detail: StoreDetail): StoreDraft {
  return {
    name: detail.name,
    mode: detail.mode,
    code: detail.code,
    webhookSilenceHours:
      detail.webhookSilenceHours === null ? '' : String(detail.webhookSilenceHours),
    blockingProductIds: detail.blockingProductIds,
    customMessage: detail.customMessage
      ? {
          enabled: true,
          productId: detail.customMessage.productId,
          productTitle: detail.customMessage.productId,
          propertyName: detail.customMessage.propertyName,
          column: detail.customMessage.column,
        }
      : { enabled: false, productId: '', productTitle: '', propertyName: '', column: 7 },
    defaultCarrierCode: detail.defaultCarrier.code,
    defaultCarrierLabel: detail.defaultCarrier.label,
  };
}

function comparableDraft(draft: StoreDraft): StoreDraft {
  return { ...draft, customMessage: { ...draft.customMessage, productTitle: '' } };
}

/**
 * Fiche boutique — `02` §12.2. Un seul formulaire couvre toutes les sections
 * (mode, offset, produits spéciaux, transporteur par défaut)
 * et envoie l'objet complet à `PUT /api/stores/:code` en une fois (l'API
 * accepte aussi bien un sous-ensemble que l'objet complet, `03` §3 — voir
 * `apps/server/src/http/api/stores/index.ts`). `code`/`shopDomain`/`currency`
 * sont en lecture seule : le schéma serveur n'a pas de champ pour les changer
 * après création.
 */
export function StoreDetailForm({ code, onBack }: StoreDetailFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: settingsKeys.storeDetail(code),
    queryFn: () => fetchStoreDetail(code),
  });
  const [draft, setDraft] = useState<StoreDraft | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);

  useEffect(() => {
    if (query.data) {
      setDraft(toDraft(query.data));
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: StoreUpdateBody) => updateStore(code, body),
    onSuccess: (data) => {
      toast.success(`Boutique « ${data.name} » mise à jour.`);
      queryClient.setQueryData(settingsKeys.storeDetail(code), data);
      setDraft(toDraft(data));
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const testMutation = useMutation({
    mutationFn: () => testStoreConnection(code),
    onSuccess: (data) => {
      setTestResult(data);
      if (data.ok) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  // L'erreur est testée avant le chargement : sans données, `draft` reste
  // vide et le squelette masquerait l'échec indéfiniment.
  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        <BackToListButton onBack={onBack} />
        <ErrorState
          message={
            query.error instanceof ApiError
              ? query.error.message
              : 'Une erreur technique est survenue.'
          }
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </div>
    );
  }
  if (query.isPending || !draft) {
    return (
      <div className="flex flex-col gap-6">
        <BackToListButton onBack={onBack} />
        <SettingsSectionSkeleton />
        <SettingsSectionSkeleton fields={2} />
        <SettingsSectionSkeleton />
      </div>
    );
  }

  const detail = query.data;
  // Le titre du produit « mot personnalisé » n'est qu'un libellé d'affichage
  // (l'API ne stocke que l'id) : il n'entre pas dans la comparaison.
  const isDirty = !isSameValue(comparableDraft(draft), comparableDraft(toDraft(detail)));

  function addBlockingProduct(product: ShopifyProduct): void {
    setDraft((prev) => {
      if (!prev) {
        return prev;
      }
      if (prev.blockingProductIds.some((entry) => entry.productId === product.productId)) {
        return prev;
      }
      return {
        ...prev,
        blockingProductIds: [
          ...prev.blockingProductIds,
          { productId: product.productId, title: product.title },
        ],
      };
    });
  }

  function removeBlockingProduct(productId: string): void {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            blockingProductIds: prev.blockingProductIds.filter(
              (entry) => entry.productId !== productId,
            ),
          }
        : prev,
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!draft) {
      return;
    }
    let webhookSilenceHours: number | null = null;
    if (draft.webhookSilenceHours.trim() !== '') {
      const parsed = Number(draft.webhookSilenceHours);
      if (!Number.isInteger(parsed) || parsed < 1) {
        toast.error('Le seuil de silence webhook doit être un entier positif (ou vide).');
        return;
      }
      webhookSilenceHours = parsed;
    }
    if (
      draft.customMessage.enabled &&
      (!draft.customMessage.productId || !draft.customMessage.propertyName.trim())
    ) {
      toast.error('Le mot personnalisé nécessite un produit et un nom de propriété.');
      return;
    }

    const body: StoreUpdateBody = {
      code: draft.code.trim(),
      name: draft.name,
      mode: draft.mode,
      webhookSilenceHours,
      blockingProductIds: draft.blockingProductIds,
      customMessage: draft.customMessage.enabled
        ? {
            productId: draft.customMessage.productId,
            propertyName: draft.customMessage.propertyName.trim(),
            column: draft.customMessage.column,
          }
        : null,
      defaultCarrier: { code: draft.defaultCarrierCode, label: draft.defaultCarrierLabel },
    };
    mutation.mutate(body);
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <BackToListButton onBack={onBack} />

      <PageSection title="Identité">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Code de la boutique"
            htmlFor="store-code"
            hint="Dérivé du pays de facturation à l’installation. Identifie la boutique partout (commandes, règles, opérations, journal, filtres) : modifiable tant que rien ne s’y rattache."
          >
            <Input
              id="store-code"
              value={draft.code}
              onChange={(event) => {
                setDraft((prev) => (prev ? { ...prev, code: event.target.value } : prev));
              }}
            />
          </Field>
          <Field label="Domaine" hint="Adresse Shopify de cette origine, lue lors de son installation.">
            <p className="flex h-9 items-center text-sm">{detail.shopDomain}</p>
          </Field>
          <Field label="Nom" htmlFor="store-name" hint="Nom affiché dans les listes, les filtres et la santé des services.">
            <Input
              id="store-name"
              value={draft.name}
              onChange={(event) => {
                setDraft((prev) => (prev ? { ...prev, name: event.target.value } : prev));
              }}
            />
          </Field>
          <Field label="Devise">
            <p className="flex h-9 items-center text-sm text-muted-foreground">
              {detail.currency ?? '— (lue de Shopify après installation)'}
            </p>
          </Field>
        </div>
      </PageSection>



      <PageSection title="Activation">
        <label className="flex items-center gap-3 text-sm font-medium">
          <Switch checked={draft.mode !== 'paused'} onCheckedChange={(active) => {
            setDraft((prev) => prev ? { ...prev, mode: active ? 'active' : 'paused' } : prev);
          }} />
          Boutique {draft.mode === 'paused' ? 'inactive' : 'active'}
        </label>
        <p className="mt-2 text-xs text-muted-foreground">Une boutique inactive ne collecte pas de nouvelles commandes. Utilisez le rattrapage après sa réactivation.</p>
      </PageSection>

      <PageSection title="Transporteur par défaut">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Code" htmlFor="default-carrier-code">
            <Input
              id="default-carrier-code"
              value={draft.defaultCarrierCode}
              onChange={(event) => {
                setDraft((prev) =>
                  prev ? { ...prev, defaultCarrierCode: event.target.value } : prev,
                );
              }}
            />
          </Field>
          <Field label="Libellé" htmlFor="default-carrier-label">
            <Input
              id="default-carrier-label"
              value={draft.defaultCarrierLabel}
              onChange={(event) => {
                setDraft((prev) =>
                  prev ? { ...prev, defaultCarrierLabel: event.target.value } : prev,
                );
              }}
            />
          </Field>
        </div>
      </PageSection>

      <PageSection title="Alertes">
        <Field label="Alerter si aucune commande depuis (heures)" htmlFor="store-webhook-silence"
          hint="Laisser vide pour utiliser le seuil de 48 heures.">
          <Input id="store-webhook-silence" type="number" min={1} placeholder="48"
            value={draft.webhookSilenceHours}
            onChange={(event) => setDraft((prev) => prev ? { ...prev, webhookSilenceHours: event.target.value } : prev)} />
        </Field>
      </PageSection>

      <CatchupSection source="shopify" store={code} />





      <PageSection
        title="Installation Shopify"
        actions={<StatusBadge registry={CONNECTION_STATUS} code={detail.connection.status} />}
      >
        {detail.connection.status === 'not_installed' ? (
          <a href={installUrl(code)} className={buttonVariants({ variant: 'default' })}>
            Installer sur la boutique
          </a>
        ) : (
          <div className="flex flex-col gap-4">
            <DescriptionList>
              <DescriptionItem label="Installée le">
                <span className="whitespace-nowrap tabular-nums">
                  {detail.connection.installedAt
                    ? formatParisDateTime(new Date(detail.connection.installedAt))
                    : '—'}
                </span>
              </DescriptionItem>
              <DescriptionItem label="Scopes accordés">
                {detail.connection.grantedScopes.length > 0 ? (
                  <span className="flex flex-wrap gap-1">
                    {detail.connection.grantedScopes.map((scope) => (
                      <Badge key={scope} tone="gray">
                        {scope}
                      </Badge>
                    ))}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Aucun.</span>
                )}
              </DescriptionItem>
              {detail.connection.missingScopes.length > 0 ? (
                <DescriptionItem label="Scopes manquants">
                  <span className="flex flex-wrap gap-1">
                    {detail.connection.missingScopes.map((scope) => (
                      <Badge key={scope} tone="orange">
                        {scope}
                      </Badge>
                    ))}
                  </span>
                </DescriptionItem>
              ) : null}
            </DescriptionList>
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
              <a href={installUrl(code)} className={buttonVariants({ variant: 'outline' })}>
                Réinstaller
              </a>
              <Button
                type="button"
                variant="outline"
                isLoading={testMutation.isPending}
                onClick={() => {
                  testMutation.mutate();
                }}
              >
                Tester la connexion
              </Button>
            </div>
            {testResult ? (
              <ConnectionTestMessage
                ok={testResult.ok}
                message={testResult.message}
                detail={testResult.detail}
                durationMs={testResult.durationMs}
              />
            ) : null}
          </div>
        )}
      </PageSection>

      <PageSection title="Produits spéciaux">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold">Produits bloquants</h3>
            {draft.blockingProductIds.length > 0 ? (
              <ul className="divide-y divide-border rounded-md bg-muted/50">
                {draft.blockingProductIds.map((product) => (
                  <li
                    key={product.productId}
                    className="flex min-h-10 items-center justify-between gap-2 py-1 pl-3 pr-1 text-sm"
                  >
                    <span className="min-w-0 break-words">
                      {product.title || product.productId}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Retirer ${product.title || product.productId}`}
                      onClick={() => {
                        removeBlockingProduct(product.productId);
                      }}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Aucun produit bloquant.</p>
            )}
            <div>
              <ProductSearchPicker storeCode={code} onSelect={addBlockingProduct} />
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-border pt-5">
            <label className="flex w-fit items-center gap-2.5 text-sm font-medium">
              <Switch
                checked={draft.customMessage.enabled}
                onCheckedChange={(checked) => {
                  setDraft((prev) =>
                    prev
                      ? { ...prev, customMessage: { ...prev.customMessage, enabled: checked } }
                      : prev,
                  );
                }}
              />
              Activer le mot personnalisé
            </label>
            {draft.customMessage.enabled ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-sm">
                    Produit :{' '}
                    {draft.customMessage.productTitle ||
                      draft.customMessage.productId ||
                      'Aucun sélectionné'}
                  </span>
                  <ProductSearchPicker
                    storeCode={code}
                    triggerLabel="Choisir le produit"
                    onSelect={(product) => {
                      setDraft((prev) =>
                        prev
                          ? {
                              ...prev,
                              customMessage: {
                                ...prev.customMessage,
                                productId: product.productId,
                                productTitle: product.title,
                              },
                            }
                          : prev,
                      );
                    }}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nom de la propriété" htmlFor="custom-message-property">
                    <Input
                      id="custom-message-property"
                      value={draft.customMessage.propertyName}
                      onChange={(event) => {
                        setDraft((prev) =>
                          prev
                            ? {
                                ...prev,
                                customMessage: {
                                  ...prev.customMessage,
                                  propertyName: event.target.value,
                                },
                              }
                            : prev,
                        );
                      }}
                    />
                  </Field>
                  <Field label="Colonne cible" htmlFor="custom-message-column">
                    <Select
                      value={String(draft.customMessage.column)}
                      onValueChange={(next) => {
                        setDraft((prev) =>
                          prev
                            ? {
                                ...prev,
                                customMessage: {
                                  ...prev.customMessage,
                                  column: next === '7' ? 7 : 8,
                                },
                              }
                            : prev,
                        );
                      }}
                    >
                      <SelectTrigger id="custom-message-column">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Colonne 7</SelectItem>
                        <SelectItem value="8">Colonne 8</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </PageSection>

      <div className="flex flex-wrap justify-end gap-2">
        <SaveButton isLoading={mutation.isPending} isDirty={isDirty}>
          Enregistrer les modifications
        </SaveButton>
      </div>
    </form>
  );
}
